<script setup>
import {computed, ref, useTemplateRef, watch, onUnmounted} from "vue";
import {QrcodeStream} from "qrcode-reader-vue3";
import {mdiQrcodeScan, mdiFlashlight, mdiFlashlightOff, mdiMagnifyPlusOutline, mdiMagnifyMinusOutline} from "@mdi/js";
import {pgettext} from "@/utils/translate";

// State machine:
//
//  closed ──[openDialog]──► open
//    ▲                        │
//    └──[cancel / scan done]──┘
//
// Inside "open":
//   openToken     bumped on every openDialog/closeDialog/unmount. Async
//                 continuations (priming, @init, debounced zoom) capture it
//                 and bail if it moved — guards against a stale resolution
//                 writing into the NEXT dialog session.
//   streamReady   false ──[primeCameraPermission resolves, token still current]──► true
//                 gates <QrcodeStream> itself: false unmounts it (releasing
//                 the camera), true mounts a fresh instance.
//   cameraLoading true ──[@init resolves/rejects, or toggleTorch restarts]──► false
//                 also gates .camera-controls, so a torch-triggered camera
//                 restart (the library's `watch: { torch() { this.init() } }`)
//                 shows the spinner instead of a dead-looking control.
//   cameraError   false ──[@init rejects]──► true
//   scanning      false ──[onDecode / confirmManual]──► true ──► emit+close

const SCANNER_THRESHOLD = 50; // ms between chars — USB scanner threshold

// QrcodeStream scans every 500ms unless a `track` function is supplied, which
// switches it to ~25fps (40ms). We want the rate, not the overlay drawing.
const noopTrack = () => {};

const props = defineProps({
  mode: {
    type: String,
    required: false,
    default: 'qr'
  },
  icon: {
    type: String,
    required: false,
    default: mdiQrcodeScan
  }
})
const emit = defineEmits(['scan']);

const formats = computed(() => {
  switch (props.mode) {
    case 'bar':
      return ['data_matrix', 'ean_13', 'ean_8', 'itf', 'pdf417', 'upc_a', 'upc_e'];
    default:
      return null;
  }
})

const dialogOpen = ref(false);
const cameraError = ref(false);
const cameraLoading = ref(true);
const manualInput = ref('');
const scanning = ref(false);
const scanSuccess = ref(false);
const torchOn = ref(false);
const zoomLevel = ref(1);
const zoomCapabilities = ref(null); // null = device doesn't support zoom
const focusSupported = ref(false);
// Monotonic session id. Every async continuation captures it and bails if it
// moved — otherwise a stale resolution writes into the NEXT dialog session.
const openToken = ref(0);
// Gates <QrcodeStream> in the template. False unmounts it immediately
// (releasing the camera at close/reopen); true mounts a fresh instance only
// once permission has been primed.
const streamReady = ref(false);
let zoomTimer = null;

const manualInputRef = useTemplateRef('manualInputRef');
const qrcodeRef = useTemplateRef('qrcodeRef');

// USB scanner auto-commit state
let inputStartTimestamp = null;
let commitTimeout = null;

// Instrumentation timestamps (module-scope, not reactive — never rendered)
let dialogOpenedAt = null;
let cameraReadyAt = null;

watch(manualInput, (newValue) => {
  if (!newValue) {
    return;
  }

  if (inputStartTimestamp === null) {
    inputStartTimestamp = (new Date()).getTime();
  }

  if (commitTimeout !== null) {
    clearTimeout(commitTimeout);
  }

  if (newValue.length < 3) {
    return;
  }

  const now = (new Date()).getTime();
  const diff = Math.max(now - inputStartTimestamp, 1);
  const msBetweenChars = diff / newValue.length;

  if (msBetweenChars <= SCANNER_THRESHOLD) {
    commitTimeout = setTimeout(() => {
      confirmManual();
    }, 5 * SCANNER_THRESHOLD);
  }
});

// TIC card format: PLK_P_1234 → P1234
// Split by '_', discard first segment (region), join remainder (prefix+number)
function cleanTicCardNumber(value) {
  const parts = value.split('_');
  if (parts.length >= 3) {
    return {
      ticPrefix: parts[1],
      ticNumber: parts[2],
      tic: parts.slice(1).join('')
    }
  }
  return value;
}

// Device IDs are blank until camera permission is granted for the origin. The
// library branches on device COUNT alone, so blank IDs can produce
// {deviceId:{exact:""}} -> OverconstrainedError, not a graceful fallback.
// Take a throwaway stream first so enumerateDevices() is populated.
async function primeCameraPermission() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter(d => d.kind === 'videoinput');
    if (videoInputs.length > 0 && videoInputs.every(d => d.deviceId)) return;

    const stream = await navigator.mediaDevices.getUserMedia({video: true});
    stream.getTracks().forEach(t => t.stop());
  } catch {
    // Denied or unavailable — let QrcodeStream surface the error normally.
  }
}

async function openDialog() {
  // Reset all state fresh on every open
  openToken.value++;
  const token = openToken.value;
  scanning.value = false;
  manualInput.value = '';
  cameraError.value = false;
  cameraLoading.value = true;
  scanSuccess.value = false;
  torchOn.value = false;
  zoomLevel.value = 1;
  zoomCapabilities.value = null;
  focusSupported.value = false;
  streamReady.value = false;
  inputStartTimestamp = null;
  if (commitTimeout !== null) {
    clearTimeout(commitTimeout);
    commitTimeout = null;
  }

  // Show the dialog first so the spinner is visible and the permission prompt
  // appears in context rather than over the dashboard.
  dialogOpen.value = true;
  dialogOpenedAt = performance.now();
  cameraReadyAt = null;

  await primeCameraPermission();
  // Closed or reopened while priming was in flight — do not mount a camera
  // into a dead dialog.
  if (token !== openToken.value) return;
  streamReady.value = true;
}

function closeDialog() {
  // Invalidate anything in flight, and drop a pending zoom apply.
  openToken.value++;
  if (zoomTimer !== null) {
    clearTimeout(zoomTimer);
    zoomTimer = null;
  }
  if (commitTimeout !== null) {
    clearTimeout(commitTimeout);
    commitTimeout = null;
  }
  scanning.value = false;
  manualInput.value = '';
  cameraError.value = false;
  scanSuccess.value = false;
  streamReady.value = false;
  inputStartTimestamp = null;
  dialogOpen.value = false;
}

// qrcode-reader-vue3 emits @decode with the string content directly.
function onDecode(rawValue) {
  if (scanning.value) return;
  if (!rawValue) return;

  scanning.value = true;
  scanSuccess.value = true;

  // Two deltas: total user-perceived latency, and pure scan time once the
  // camera is live. The resolution patch and the raised scan rate are what
  // should move the second one.
  if (dialogOpenedAt !== null) {
    console.info('QrScanButton timeToFirstRead', {
      fromOpen: Math.round(performance.now() - dialogOpenedAt),
      fromCameraReady: cameraReadyAt === null
        ? null
        : Math.round(performance.now() - cameraReadyAt)
    });
  }

  setTimeout(() => {
    var value = rawValue
    if (props.mode === 'qr') {
      value = cleanTicCardNumber(rawValue)
    }
    emit('scan', value);
    closeDialog();
  }, 300);
}

// qrcode-reader-vue3 uses @init instead of @camera-on/@error.
// The emitted promise resolves with capabilities on success, rejects on camera error.
async function onInit(promise) {
  const token = openToken.value;
  try {
    await promise;
    if (token !== openToken.value) return;
    cameraLoading.value = false;
    cameraReadyAt = performance.now();
    await applyZoomAndFocus(token);
    if (token !== openToken.value) return;
    await logTrackState('camera-on');
  } catch {
    if (token !== openToken.value) return;
    cameraError.value = true;
    cameraLoading.value = false;
  }
}

// Get the active video track from inside the QrcodeStream component.
function getVideoTrack() {
  const el = qrcodeRef.value?.$el;
  if (!el) return null;
  const video = el.querySelector('video');
  const stream = video?.srcObject;
  if (!stream) return null;
  const [track] = stream.getVideoTracks();
  return track ?? null;
}

// Reads back what the browser ACTUALLY negotiated. Constraints are requests,
// not guarantees, so every claim in the design doc is proven or disproven here.
async function logTrackState(phase) {
  // Instrumentation must never be able to fake a camera failure: everything
  // below is best-effort diagnostics, so any throw here (sync or async) is
  // logged and swallowed rather than propagating into onInit's catch.
  try {
    const s = getVideoTrack()?.getSettings?.();
    if (!s) return;
    // deviceId alone is opaque; correlate against the labelled device list
    // (populated once permission is granted) to identify WHICH lens is live.
    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
    const videoInputs = devices.filter(d => d.kind === 'videoinput');
    const active = videoInputs.find(d => d.deviceId === s.deviceId);
    console.info('QrScanButton track', phase, {
      width: s.width,
      height: s.height,
      aspect: s.aspectRatio,
      zoom: s.zoom,
      focusMode: s.focusMode,
      lens: active?.label || '(unlabelled)',
      // Proves whether the library's "last videoinput is rear" heuristic held.
      lensIndex: videoInputs.findIndex(d => d.deviceId === s.deviceId),
      lensCount: videoInputs.length
    });
  } catch (e) {
    console.warn('QrScanButton: logTrackState failed', e);
  }
}

// applyConstraints REPLACES the track's constraint set — it does not merge.
// Every call must therefore restate everything we still want applied.
//
// KNOWN EXCEPTION — torch is NOT restated here. The library applies torch
// itself, inside its own Camera(...) call, before that promise resolves; the
// applyConstraints call built from this function then runs afterward (e.g.
// from onInit's applyZoomAndFocus) and REPLACES the set, potentially
// dropping torch on devices where zoom or focus constraints are also
// applied. This is a known, deliberately deferred gap — it needs a physical
// device to verify one way or the other — not a guarantee that torch
// survives. Do not treat "every call restates everything" as true for torch.
function buildTrackConstraints() {
  // Separate entries, not one merged set: each `advanced` entry is
  // all-or-nothing, so merging would mean a device without continuous focus
  // also loses zoom. Independent entries let each apply on its own.
  //
  // No width/height here on purpose: resolution is negotiated once, at
  // getUserMedia, via the patch-package patch of the library's constraints
  // (patches/qrcode-reader-vue3+4.1.0.patch). Restating it mid-stream risks
  // an active renegotiation — a black frame or focus reset — to defend
  // against a drift that leaving it unconstrained is unlikely to cause.
  const advanced = [];
  if (zoomCapabilities.value) advanced.push({zoom: zoomLevel.value});
  if (focusSupported.value) advanced.push({focusMode: 'continuous'});
  return advanced.length ? {advanced} : null;
}

async function applyTrackConstraints(token) {
  if (token !== openToken.value) return;
  const track = getVideoTrack();
  if (!track) return;
  const constraints = buildTrackConstraints();
  if (!constraints) return;
  try {
    await track.applyConstraints(constraints);
  } catch (e) {
    console.warn('QrScanButton: track constraints failed', e);
  }
}

// Reads capabilities, sets defaults, then applies zoom AND focus in a single
// call so neither discards the other.
//
// Takes the caller's captured `token` rather than reading `openToken.value`
// itself: applyTrackConstraints's own guard (`if (token !== openToken.value)
// return`) only protects against a session change that happened BEFORE this
// call, and reading the live value here would make that guard a tautology —
// it can never see a mismatch, because there is no `await` between capturing
// it and using it. It only looks safe today because nothing has raced it
// yet; passing the caller's token is what keeps the guard meaningful if a
// future edit adds one.
async function applyZoomAndFocus(token) {
  const track = getVideoTrack();
  if (!track) return;

  const cap = track.getCapabilities?.() ?? {};

  if (cap.zoom) {
    zoomCapabilities.value = cap.zoom;
    // Start at 2x or the device max, whichever is smaller.
    zoomLevel.value = Math.min(2, cap.zoom.max);
  }
  focusSupported.value = !!cap.focusMode?.includes('continuous');

  await applyTrackConstraints(token);
}

// The slider emits continuously while dragged. One applyConstraints per tick
// serializes a backlog and provokes repeated renegotiation on mobile, so the
// call is trailing-debounced. Debounce rather than Vuetify's @end event so
// keyboard and accessibility input, which may not emit `end`, still applies.
function onZoomChange() {
  const token = openToken.value;
  if (zoomTimer !== null) clearTimeout(zoomTimer);
  zoomTimer = setTimeout(async () => {
    zoomTimer = null;
    // Debouncing alone is not enough: a pending timer can fire after close,
    // and getVideoTrack() reads whatever qrcodeRef holds NOW — which would
    // apply the old session's zoom to a new stream.
    await applyTrackConstraints(token);
    if (token !== openToken.value) return;
    await logTrackState('after-zoom');
  }, 150);
}

// Toggling torch triggers the library's `watch: { torch() { this.init() } }`,
// which stops and restarts the camera (~700ms). Without this, .camera-controls
// stays enabled through the restart (it's only gated on the initial
// cameraLoading), so a second tap lands while cameraInstance is still null —
// the library's own beforeResetCamera() guard can't stop a stream that
// hasn't been assigned yet, and the first getUserMedia stream is orphaned,
// never stopped. Raising cameraLoading here disables the controls and shows
// the spinner for the restart's duration; onInit clears it again on @init.
function toggleTorch() {
  cameraLoading.value = true;
  torchOn.value = !torchOn.value;
}

function confirmManual() {
  if (!manualInput.value || scanning.value) return;

  scanning.value = true;
  emit('scan', cleanTicCardNumber(manualInput.value));
  closeDialog();
}

// Auto-focus manual input when dialog opens; suppress mobile virtual keyboard
watch(manualInputRef, (newVal) => {
  if (newVal) {
    const input = newVal.$el.querySelector('input');
    if (input) input.setAttribute('inputmode', 'none');
    newVal.focus();
  }
});

const label = computed(()=>{
  if (props.mode==='bar'){
    return pgettext('label', 'Scan bar code')
  }
  return pgettext('label', 'Scan QR code')
})

// The library stops its own stream on unmount, but a pending zoom timer and
// any in-flight async continuation are ours to cancel.
onUnmounted(() => {
  openToken.value++;
  if (zoomTimer !== null) {
    clearTimeout(zoomTimer);
    zoomTimer = null;
  }
  if (commitTimeout !== null) {
    clearTimeout(commitTimeout);
    commitTimeout = null;
  }
});

// Expose cleanTicCardNumber for unit tests
defineExpose({cleanTicCardNumber, openDialog, onDecode, onInit, confirmManual});
</script>

<template>
  <v-tooltip :text="label">
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        :append-icon="icon"
        size="large"
        color="purple"
        block
        :aria-label="label"
        :text="label"
        @click="openDialog"
      ></v-btn>
    </template>
  </v-tooltip>

  <v-dialog v-model="dialogOpen" max-width="480px" @update:modelValue="val => !val && closeDialog()">
    <v-card>
      <v-card-title>{{ label }}</v-card-title>
      <v-card-text>
        <!-- Camera area -->
        <div v-if="!cameraError" class="camera-area mb-4">
          <div v-if="cameraLoading" class="d-flex flex-column align-center justify-center py-6">
            <v-progress-circular indeterminate color="primary" class="mb-2"/>
            <span>{{ pgettext('QrScanButton', 'Camera starting...') }}</span>
            <span class="text-caption text-medium-emphasis mt-1">
                            {{ pgettext('QrScanButton', 'Allow camera access if prompted') }}
                        </span>
          </div>

          <div class="qrcode-stream-wrapper" role="img"
               :aria-label="pgettext('QrScanButton', 'Live camera feed for QR scanning')">

            <QrcodeStream
              v-if="streamReady"
              ref="qrcodeRef"
              camera="auto"
              :formats="formats"
              :torch="torchOn"
              :track="noopTrack"
              @decode="onDecode"
              @init="onInit"
            />
            <div v-if="scanSuccess" class="scan-success-overlay"/>
          </div>

          <!-- Camera controls: torch + zoom slider -->
          <div v-if="!cameraLoading" class="camera-controls mt-2 d-flex align-center ga-2">
            <v-btn
              :icon="torchOn ? mdiFlashlight : mdiFlashlightOff"
              density="comfortable"
              variant="tonal"
              :color="torchOn ? 'amber' : undefined"
              :aria-label="pgettext('QrScanButton', 'Toggle torch')"
              @click="toggleTorch"
            />
            <v-slider
              v-if="zoomCapabilities"
              v-model="zoomLevel"
              :min="zoomCapabilities.min"
              :max="zoomCapabilities.max"
              :step="zoomCapabilities.step || 0.1"
              hide-details
              density="compact"
              color="purple"
              :prepend-icon="mdiMagnifyMinusOutline"
              :append-icon="mdiMagnifyPlusOutline"
              :aria-label="pgettext('QrScanButton', 'Zoom')"
              @update:modelValue="onZoomChange"
            />
          </div>
        </div>

        <v-alert v-if="cameraError" type="warning" variant="tonal" density="compact" class="mb-4">
          {{ pgettext('QrScanButton', 'Camera not available') }}
        </v-alert>

        <!-- Accessible scan result announcement -->
        <div role="status" aria-live="polite" class="sr-only">
          <span v-if="scanSuccess">{{ pgettext('QrScanButton', 'QR code scanned successfully') }}</span>
        </div>

        <!-- Manual / USB scanner input — always visible -->
        <v-text-field
          ref="manualInputRef"
          v-model="manualInput"
          :label="pgettext('QrScanButton', 'Or enter / scan card number:')"
          variant="outlined"
          density="compact"
          hide-details="auto"
          @keydown.enter.prevent="confirmManual"
          @keydown.tab.prevent="confirmManual"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn variant="text" @click="closeDialog">
          {{ pgettext('QrScanButton', 'Cancel') }}
        </v-btn>
        <v-btn
          color="success"
          variant="elevated"
          :disabled="!manualInput"
          @click="confirmManual"
        >
          {{ pgettext('QrScanButton', 'Confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.camera-area {
  position: relative;
}

.qrcode-stream-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
}

.scan-success-overlay {
  position: absolute;
  inset: 0;
  background: rgba(76, 175, 80, 0.4);
  z-index: 1;
  pointer-events: none;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>